/**
 * ADR-0185 §3.3 — the Next-Up rail, the board's front face: one line per
 * machine — machine · running now · next · the changeover into it (colored by
 * measured cost, estimated flagged) · setup hours · proposed start. A red
 * badge marks a next job whose promise is at risk. Clicking a line expands
 * that machine's lane. The changeover chip carries the board's hover card
 * (never a native title): the transition, class, hours, and estimated flag.
 */
import { useState } from "react";
import { formatTimestamp } from "../../../../lib/formatTime.js";
import type { BoardLane, BoardLaneJob } from "../../boardTypes.js";
import { ChangeoverHoverCard } from "./ChangeoverHoverCard.js";
import { deriveNextUp, hoverCardPosition, maxSetupHours, setupColor } from "./laneMath.js";

type HoverState = { job: BoardLaneJob; x: number; y: number; cw: number; ch: number };

export function NextUpRail(props: {
  lanes: BoardLane[];
  /** class label → measured changeover count for the hover card's sample line. */
  measuredCounts?: Map<string, number | null>;
  selectedMachine: string | null;
  onSelect: (machine: string | null) => void;
}) {
  const [hover, setHover] = useState<HoverState | null>(null);
  const rows = deriveNextUp(props.lanes);
  const max = maxSetupHours(props.lanes);
  if (rows.length === 0) return null;
  return (
    <div className="boardRail" role="list">
      <div className="boardRailHeader" aria-hidden="true">
        <span>Machine</span>
        <span>Running now</span>
        <span />
        <span>Next up</span>
        <span>Changeover</span>
        <span className="boardRailStart">Starts</span>
      </div>
      {rows.map((row) => {
        const next = row.next;
        const selected = props.selectedMachine === row.machine;
        return (
          <button
            key={row.machine}
            type="button"
            role="listitem"
            className={`boardRailRow${selected ? " boardRailRowSelected" : ""}`}
            onClick={() => props.onSelect(selected ? null : row.machine)}
            aria-expanded={selected}
          >
            <span className="boardRailMachine">{row.machine}</span>
            <span className="boardRailNow">
              {row.running ? (
                <>
                  <span className="mono">{row.running.job}</span>
                  {row.running.part ? <span className="muted"> · {row.running.part}</span> : null}
                </>
              ) : (
                <span className="muted">idle</span>
              )}
            </span>
            <span className="boardRailArrow" aria-hidden="true">→</span>
            <span className="boardRailNext">
              {next ? (
                <>
                  <span className="mono">{next.job}</span>
                  {next.part ? <span className="muted"> · {next.part}</span> : null}
                  {next.at_risk ? (
                    <span className="pill pillBad boardRailBadge" title="This job's promise is at risk in the plan">
                      At risk
                    </span>
                  ) : null}
                </>
              ) : (
                <span className="muted">nothing queued</span>
              )}
            </span>
            <span className="boardRailSetup">
              {next && next.setup_class ? (
                <>
                  <span
                    className="boardSetupChip"
                    style={{ background: setupColor(next.setup_hours, max) }}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.closest(".boardRail")!.getBoundingClientRect();
                      setHover({ job: next, x: e.clientX - rect.left, y: e.clientY - rect.top, cw: rect.width, ch: rect.height });
                    }}
                    onMouseLeave={() => setHover(null)}
                  >
                    {next.setup_class}
                  </span>
                  {next.setup_cost_fallback ? (
                    <span
                      className="pill pillWarn boardRailBadge"
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.closest(".boardRail")!.getBoundingClientRect();
                        setHover({ job: next, x: e.clientX - rect.left, y: e.clientY - rect.top, cw: rect.width, ch: rect.height });
                      }}
                      onMouseLeave={() => setHover(null)}
                    >
                      estimated
                    </span>
                  ) : null}
                  <span className="boardRailHours">{next.setup_hours} h</span>
                </>
              ) : next ? (
                <span className="muted">no changeover</span>
              ) : null}
            </span>
            <span className="boardRailStart muted">{next?.start ? formatTimestamp(next.start) : ""}</span>
          </button>
        );
      })}
      {hover ? (
        <ChangeoverHoverCard
          job={hover.job}
          measuredCounts={props.measuredCounts}
          place={hoverCardPosition(hover.x, hover.y, hover.cw, hover.ch)}
        />
      ) : null}
    </div>
  );
}
